import React from "react";
import { Navigate, useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { useOrg } from "@/context/OrgContext";
import { useAuth } from "@/context/AuthContext";
import { PageLoading } from "@/components/common/Loading";
import { moduleEnabled } from "@/lib/constants";
import { Button } from "@/components/ui/button";

const MODULE_LABELS = {
  plan: "My Plan", weekly_focus: "Weekly Focus", actions: "Actions", scorecard: "Scorecard",
  coaching: "Coaching", business_health: "Business Health", issues: "Issues & Decisions",
  resources: "Resources", ask_amden: "Ask Amden", reports: "Reports", team_workspace: "Team Workspace",
};

// Combines RequireOrg + per-client module access. Amden staff always pass.
export function ModuleGate({ moduleKey, staffNoOrgOk = false, children }) {
  const { orgId, loading, currentOrg } = useOrg();
  const { isAmden } = useAuth();
  if (loading) return <PageLoading />;
  if (isAmden) {
    // Amden staff bypass module gating; workspace modules still need a client selected.
    if (!orgId && !staffNoOrgOk) return <Navigate to="/clients" replace />;
    return children;
  }
  if (!orgId) return <PageLoading />;
  if (!moduleEnabled(currentOrg, moduleKey)) {
    return <LockedModule moduleKey={moduleKey} org={currentOrg} />;
  }
  return children;
}

export function LockedModule({ moduleKey, org }) {
  const navigate = useNavigate();
  const label = MODULE_LABELS[moduleKey] || "This area";
  const consultant = org?.consultant_name;
  return (
    <div className="flex min-h-[60vh] items-center justify-center" data-testid="locked-module">
      <div className="max-w-md text-center">
        <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#0B1729] text-[#FFC24D]">
          <Icons.Lock size={28} />
        </div>
        <div className="label-eyebrow mb-2 justify-center">Access restricted</div>
        <h2 className="font-display text-2xl font-bold text-[#0B1729]">{label} is turned off</h2>
        <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-[#1E2D44]/60">
          {label} isn't enabled for your organisation right now.
          {consultant ? ` Ask your Amden consultant, ${consultant}, to enable it.` : " Ask your Amden consultant to enable it."}
        </p>
        <Button onClick={() => navigate("/")} className="mt-6 h-11 bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="locked-back-home">
          <Icons.ArrowLeft size={16} className="mr-1.5" /> Back to Home
        </Button>
      </div>
    </div>
  );
}
